import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus } from 'lucide-react';

import { useAdminCreateApplePayRegistration } from '@/lib/api/apple-pay-registrations';

export function CreateRegistrationDialog() {
  const create = useAdminCreateApplePayRegistration();
  const [open, setOpen] = React.useState(false);
  const [targetAccountId, setTargetAccountId] = React.useState('');
  const [merchantIdentifier, setMerchantIdentifier] = React.useState('');
  const [displayName, setDisplayName] = React.useState('');
  const [associationFileContent, setAssociationFileContent] = React.useState('');

  // Account is optional: leaving it blank creates a shared PLATFORM registration
  // whose domains span many accounts (attributed per-domain later).
  const canSubmit =
    /^merchant\./.test(merchantIdentifier.trim()) &&
    !!displayName.trim() &&
    displayName.trim().length <= 64 &&
    !create.isPending;

  const reset = () => {
    setTargetAccountId('');
    setMerchantIdentifier('');
    setDisplayName('');
    setAssociationFileContent('');
  };

  const handleFile = async (file: File | null) => {
    if (!file) return;
    // The Apple association file is a small plain-text blob — read it directly.
    const text = await file.text();
    setAssociationFileContent(text.trim());
  };

  const handleSubmit = () => {
    create.mutate(
      {
        targetAccountId: targetAccountId.trim() || undefined,
        merchantIdentifier: merchantIdentifier.trim(),
        displayName: displayName.trim(),
        associationFileContent: associationFileContent.trim() || undefined,
      },
      {
        onSuccess: () => {
          reset();
          setOpen(false);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : (setOpen(false), reset()))}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="mr-2 h-4 w-4" />
          New registration
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Apple Pay registration</DialogTitle>
          <DialogDescription>
            Creates a registration at Basis Theory. Leave the account blank for a shared platform certificate
            (domains from many merchants attach to it); set an account to create a registration owned by one
            merchant. Certificates and domains are added afterwards.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Account ID — blank = shared platform certificate</Label>
            <Input
              placeholder="acc_… (leave blank for platform)"
              value={targetAccountId}
              onChange={(e) => setTargetAccountId(e.target.value)}
              className="font-mono text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Apple merchant identifier</Label>
            <Input
              placeholder="merchant.com.acme.shop"
              value={merchantIdentifier}
              onChange={(e) => setMerchantIdentifier(e.target.value)}
              className="font-mono text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Display name (a label to identify this certificate, ≤ 64 chars)</Label>
            <Input
              placeholder="ACME Store"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={64}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Apple domain-association file (optional now)</Label>
            <Input type="file" onChange={(e) => handleFile(e.target.files?.[0] ?? null)} />
            <Textarea
              placeholder="…or paste the association file contents"
              value={associationFileContent}
              onChange={(e) => setAssociationFileContent(e.target.value)}
              className="h-20 font-mono text-xs"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => (setOpen(false), reset())}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {create.isPending ? 'Creating…' : 'Create registration'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
